// Layer 1 (Data Schema) — the typed PM instances (a20-maintenance S5a).
// Shape and rows only: no derivation, no formatting, no React.
//
// A PM instance is one planned-maintenance task with a due date and a crew.
// It is the schedule's unit — the thing a planner moves, the supervisor
// dispatches, and compliance is counted over. Three groups make up the set:
//
//   1. Forward — the four rows of `bunker-hill-seed.ts`'s `Upcoming planned
//      maintenance (14 days)` RoleTable (`pm-2`…`pm-5`), typed once and
//      verbatim: same dates, durations and crews. `pm-1`, the BM01 bearing
//      R&R, left the plan with the D16 seed fix (polish.P21).
//   2. Completed — never typed here. `PmSchedule.transform.ts`'s
//      `completedFromEvents()` builds them from the downtime event log's
//      planned stoppages inside the 30-day basis, because a stopping PM is one
//      fact with two reads: the event log the Downtime, Causes, Reliability and
//      Value lost tabs already count, and the compliance denominator here. One
//      typed copy would be a second source of the same fact, and the two would
//      drift. A seed never imports a transform, so the generator lives in the
//      transform and this file stays Layer 1.
//   3. Typed history and open rows — the non-stopping PMs (inspections,
//      calibrations, lube routes) that never appear in the downtime log,
//      because the plant kept running while they were done.
//
// Status is never seeded. There is no field for it and there must not be: a
// PM's status is its due date and its completion date read against the cursor,
// so a schedule that has not been rescheduled cannot claim to be on time.
// `PmSchedule.transform.ts`'s `pmStatus()` owns that read.
//
// Two recorded facts about the generated rows:
//
//   (a) A stopping PM is due on the day its own window opens — the event's
//       start is both the due date and the moment the work began. A generated
//       instance is therefore on time by construction, and no amount of
//       rescheduling can make one late. Every point of compliance variance
//       below comes from the non-stopping typed rows, which are the only ones
//       that can slip.
//   (b) A generated row carries the `DOWNTIME_ITEMS` label for its item
//       (`BM01 Ball mill`), while the forward rows keep the source table's
//       verbatim names (`Regrind mill`). The two label vocabularies differ by source —
//       the event log's item labels and the plan's asset names — and unifying
//       them is a roadmap row, not this slice's.

import type { SeriesTime } from '@/versions/v1/schema/reading-series';

export type PmTask = {
  id: string;
  /** A MAINTAINABLE_ITEMS id (strategy.seed.ts) — ten ids, a superset of the eight DowntimeItemId values the event log carries. */
  itemId?: string;
  /** The label on the glass. Set from the asset's own source, never mapped by guess. */
  asset: string;
  task: string;
  dueDate: SeriesTime;
  estHours: number;
  crew: string;
  /** Present on a completed instance; absent on every open row. */
  completedAt?: SeriesTime;
  /** The downtime event a generated instance came from. */
  sourceEventId?: string;
};

/** The crew on a stopping PM. The downtime event log carries no crew field —
 *  a stopping PM is worked by the whole crew, so this is the one label every
 *  generated instance shares. */
// [syn] — the event log has no crew; a stopping PM is worked by the whole crew
export const SHUTDOWN_CREW_LABEL = 'Full crew';

/** The forward four (group 1) and the typed nine (group 3). The completed
 *  instances (group 2) are generated, never listed here. */
export const TYPED_TASKS: readonly PmTask[] = [
  // -- Forward: the 14-day plan, verbatim from the RoleTable's cells. --------
  // No BM01 row (D16, polish.P21): the bearing's 4 h inspection stop on Sat
  // 13 Jun is the case's draft action (`case-bm-brg` stopPlan), and it stays
  // out of the schedule until it is approved. WO-441 is the 04 Jun trip.
  {
    // [syn]
    id: 'pm-2',
    asset: 'Regrind mill',
    task: 'Liner inspection',
    dueDate: '2026-06-12T06:00',
    estHours: 4,
    crew: 'J. Smith',
  },
  {
    // [syn]
    id: 'pm-3',
    asset: 'Pb/Ag filter',
    task: 'Filter cloth change',
    dueDate: '2026-06-13T06:00',
    estHours: 8,
    crew: 'K. Jones',
  },
  {
    // [syn]
    id: 'pm-4',
    asset: 'Pb recleaner',
    task: 'Air sparger rebuild complete',
    dueDate: '2026-06-15T06:00',
    estHours: 6,
    crew: 'K. Jones',
  },
  {
    // [syn]
    id: 'pm-5',
    asset: 'Tails pump 4',
    task: 'Seal water regulator overhaul',
    dueDate: '2026-06-16T06:00',
    estHours: 5,
    crew: 'T. Brown',
  },

  // -- Completed and open: the non-stopping PMs (group 3). -------------------
  {
    // [syn]
    id: 'pm-h1',
    itemId: 'GP01',
    asset: 'GP01 Cyclone feed pump',
    task: 'Gland seal inspection',
    dueDate: '2026-05-12T08:00',
    estHours: 2,
    crew: 'J. Smith',
    completedAt: '2026-05-12T10:30',
  },
  {
    // [syn]
    id: 'pm-h2',
    itemId: 'CV-CRUSH',
    asset: 'Belt conveyors CV-001–CV-010',
    task: 'Belt tracking check',
    dueDate: '2026-05-15T08:00',
    estHours: 3,
    crew: 'T. Brown',
    completedAt: '2026-05-17T14:00',
  },
  {
    // [syn]
    id: 'pm-h3',
    itemId: 'GP40',
    asset: 'GP40 Paste pump',
    task: 'Lubrication route',
    dueDate: '2026-05-19T08:00',
    estHours: 1.5,
    crew: 'K. Jones',
    completedAt: '2026-05-19T09:30',
  },
  {
    // [syn]
    id: 'pm-h4',
    itemId: 'PB-THK',
    asset: 'Pb/Ag thickener (flocculant)',
    task: 'Flocculant dosing pump service',
    dueDate: '2026-05-28T08:00',
    estHours: 4,
    crew: 'J. Smith',
    completedAt: '2026-05-30T12:00',
  },
  {
    // [syn]
    id: 'pm-h5',
    itemId: 'ZN-RGH',
    asset: 'Zn rougher bank',
    task: 'Level transmitter calibration',
    dueDate: '2026-06-02T08:00',
    estHours: 2,
    crew: 'K. Jones',
    completedAt: '2026-06-02T10:00',
  },
  {
    // [syn]
    id: 'pm-o1',
    itemId: 'CR01',
    asset: 'CR01 Primary crusher',
    task: 'Liner wear survey',
    dueDate: '2026-06-04T08:00',
    estHours: 3,
    crew: 'J. Smith',
  },
  {
    // [syn]
    id: 'pm-t1',
    itemId: 'GP01',
    asset: 'GP01 Cyclone feed pump',
    task: 'Impeller clearance check',
    dueDate: '2026-06-08T08:00',
    estHours: 3,
    crew: 'J. Smith',
  },
  {
    // [syn]
    id: 'pm-t2',
    itemId: 'ZN-RGH',
    asset: 'Zn rougher bank',
    task: 'Air flow meter calibration',
    dueDate: '2026-06-08T08:00',
    estHours: 2,
    crew: 'K. Jones',
  },
  {
    // [syn]
    id: 'pm-t3',
    itemId: 'CV-CRUSH',
    asset: 'Belt conveyors CV-001–CV-010',
    task: 'Idler inspection',
    dueDate: '2026-06-08T08:00',
    estHours: 3,
    crew: 'T. Brown',
  },
];
